import { useQuery } from 'react-query';

import { QueryEntity } from '../types';
import { RelativityWorkspacesResponse } from '../types/backend-dto-types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig from '@/api/useGetFetchConfig';

interface UseGetRelativityWorkspacesOptions {
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
  page?: number;
  pageSize?: number;
  search?: string;
}

const useGetRelativityWorkspaces = (options: UseGetRelativityWorkspacesOptions = {}) => {
  const { enabled = true, refetchOnWindowFocus = false, page = 1, pageSize = 5, search } = options;
  const { fetchConfigGET } = useGetFetchConfig();

  const getRelativityWorkspaces = async (): Promise<RelativityWorkspacesResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.append('page', page.toString());
    searchParams.append('pageSize', pageSize.toString());
    if (search && search.trim()) {
      searchParams.append('search', search.trim());
    }

    const url = `${APIBaseChronos}/api/relativity/workspaces?${searchParams.toString()}`;
    const response = await fetch(url, fetchConfigGET);

    if (!response.ok) {
      throw new Error(`Failed to fetch Relativity workspaces: ${response.status} ${response.statusText}`);
    }

    return response.json();
  };

  return useQuery([QueryEntity.RelativityWorkspaces, page, pageSize, search], getRelativityWorkspaces, {
    enabled,
    refetchOnWindowFocus,
    keepPreviousData: true, // Keep previous data while loading new page
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors (likely auth issues)
      if (error instanceof Error && error.message.includes('401')) {
        return false;
      }
      if (error instanceof Error && error.message.includes('403')) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  });
};

export default useGetRelativityWorkspaces;
